import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import {
  fetchHospitals,
  hospitalsQueryKey,
  type HospitalListItem,
} from '../api/hospitals'
import { Footer } from '../components/Footer'
import { Icon } from '../components/Icon'
import { Image } from '../components/Image'
import { MiniHospitalCard } from '../components/MiniHospitalCard'
import { Navbar } from '../components/Navbar'
import { PartnerRow } from '../components/PartnerRow'
import { SearchInput } from '../components/SearchInput'
import { ServiceTile } from '../components/ServiceTile'
import { TILES } from '../components/tileMap'
import type { ChipSelection } from '../components/chips'
import { ICU_BED_TYPE } from '../lib/bedTypes'
import { asset } from '../lib/imageAssets'
import { useGeolocation } from '../lib/useGeolocation'

const NO_FILTER: ChipSelection = { row1: [], row2: null }

/**
 * Local-interface home — the Option B **service-tile home** (M12-T01, ADR-0026).
 * Near-zero reading: six tiles route into each workflow; the 15-chip system moves
 * to the hospital-results screen (`HospitalResults`), one tap away. Typing in the
 * search shows two deep-link suggestion rows; Enter searches hospitals on the
 * results screen. A "beds available near you now" strip reuses the existing
 * hospitals query (no new endpoint). No bottom nav in Phase 1.
 */
export function LocalHome(): ReactNode {
  const navigate = useNavigate()
  const geo = useGeolocation()
  const [q, setQ] = useState('')
  const query = useQuery({
    queryKey: hospitalsQueryKey(NO_FILTER, geo.coords),
    queryFn: ({ signal }) => fetchHospitals(NO_FILTER, geo.coords, signal),
  })
  const nearby = (query.data ?? []).slice(0, 3)
  const term = q.trim()

  // M19 — the 672px page cap is gone. It made the Local home a phone-shaped
  // column on a desktop while the Tourism home ran full width, and the Figma
  // 1440 frame (9:5968) draws this page full width too. Sections now own their
  // own measure, which is exactly how TourismHome is built.
  return (
    <div className="flex min-h-screen flex-col bg-bg-app">
      {/* M17-T04 — the hand-rolled brand header is now the shared Navbar. The
          strapline is carried through rather than dropped: it is shipped copy,
          and this milestone changes how things look, not what they say. */}
      <Navbar tagline="Every kind of care, in one place." />

      {/* Hero — ADR-0040, which amends ADR-0026's wordless front door by exactly
          one line. The heading ships alone: the Figma sub-line (FG_HERO_SUB in
          designs/screens-figma-home.jsx) was refused by ADR-0040 and is now
          ADOPTED, per ADR-0043 — the owner asked for it and the fold cost was
          measured rather than assumed.

          At a true 390x844 viewport the sub-line runs to five lines and costs
          128px, and the sixth tile's bottom edge moves from 610px to 738px. So
          ADR-0040's own hard criterion — all six tiles reachable without
          scrolling at 390x844 — still holds, with 106px to spare.

          The caveat that number hides: 844 is the CSS viewport, and a real
          phone browser spends 100-190px of it on chrome. On an actual handset
          the sixth tile is therefore borderline. If it has to go, the cheapest
          lever is `hidden sm:block` here — Figma draws the sub-line on both
          frames, so dropping it on mobile is a deviation, but a smaller one
          than losing a tile. */}
      <section className="flex flex-col items-center px-4 pt-4 sm:px-8 sm:pb-2 sm:pt-12">
        <h1 className="max-w-[660px] text-center font-display text-d-md font-medium tracking-display text-ink-1 sm:text-d-lg">
          Healthcare, Made <span className="text-accent">Easy</span> to Find
        </h1>

        <p className="max-w-[609px] pt-4 text-center text-[16px] leading-6 text-ink-2 sm:text-[18px] sm:leading-7">
          Search for specialists, reserve a hospital bed, or find a lab test
          near you in minutes. Medcor makes healthcare simple for every
          Ghanaian.
        </p>

        <form
          className="w-full max-w-[542px] pt-3 sm:pt-9"
          onSubmit={(e) => {
            e.preventDefault()
            if (term) navigate(`/?q=${encodeURIComponent(term)}`)
          }}
        >
          {/* M22-T09: a visible submit. This form NAVIGATES — it is a real
              submit, not a live filter — so the trailing button that
              `SearchInput` has always supported now has its first caller. On a
              phone, Enter alone was the only way to search, which is not an
              affordance most patients will find. */}
          <SearchInput
            value={q}
            onChange={setQ}
            label="Search hospitals, specialties, and procedures"
            placeholder="Search hospitals, specialty, procedure etc"
            onSearch={() => {
              if (term) navigate(`/?q=${encodeURIComponent(term)}`)
            }}
          />
        </form>

        {term && (
          <div className="flex w-full max-w-[542px] flex-col gap-1 pt-2">
            <SuggestionRow
              icon="user"
              onClick={() =>
                navigate(`/specialists?q=${encodeURIComponent(term)}`)
              }
            >
              Specialists matching “{term}”
            </SuggestionRow>
            <SuggestionRow
              icon="flask"
              onClick={() =>
                navigate(`/diagnostics?q=${encodeURIComponent(term)}`)
              }
            >
              Tests &amp; procedures matching “{term}”
            </SuggestionRow>
          </div>
        )}
      </section>

      {/* PORTED — FgSectionTitle: display face at --fs-d-xs, Medium, --ink-1.
          The incumbent was 10px uppercase mono on --ink-3.
          These are `h2` now rather than `p` — the hero (T16) is the page's h1,
          so the outline is legal and the sections become real landmarks.

          M17 restyled this page under a rule that it changes how things look,
          never what they say (ADR-0038), so both section titles kept their
          incumbent copy and the divergence from Figma was recorded here.
          M19-T06 reverses that on the owner's instruction: both now match the
          frame. This comment used to explain why they did not — a comment that
          documents a decision has to move when the decision does, or it becomes
          the next stale citation. */}
      <h2 className="px-4 pt-6 font-display text-d-xs font-medium tracking-display text-ink-1 sm:px-8">
        Let&rsquo;s find what you need
      </h2>
      <div className="grid grid-cols-3 gap-2.5 px-4 pt-3 sm:gap-4 sm:px-8 lg:grid-cols-6">
        {TILES.map((t) => (
          <ServiceTile key={t.label} tile={t} />
        ))}
      </div>

      {/* M19-T05 — the frame puts this ABOVE the tiles (y=562 against y=723 on
          `9:5968`). It goes below them instead, and that is deliberate: at 1440
          the frame has room, but on a 390x844 phone a seventh band above the
          tiles pushes the sixth tile past the fold, which ADR-0040's standing
          criterion and ADR-0026's whole premise forbid. The tiles are the
          navigation; a trust signal does not outrank them. */}
      <PartnerRow />

      <section aria-labelledby="nearby-h" className="px-4 pb-6 pt-6 sm:px-8">
        <h2
          id="nearby-h"
          className="font-display text-d-xs font-medium tracking-display text-ink-1"
        >
          {/* M19-T06 — the frame's wording (`65:5025`). The bed framing this
              heading used to carry is not lost: it lives in the Beds tile above
              and in each card's own emergency-and-ICU line, which is where a
              patient reads it. Do not "restore" the old copy. */}
          Nearby Hospitals
        </h2>
        <div className="mt-2.5">
          {query.isPending ? (
            <p className="text-[12px] text-ink-3" role="status">
              Finding nearby beds…
            </p>
          ) : query.isError ? null : nearby.length === 0 ? (
            <p className="text-[12px] text-ink-3">
              No hospitals available now.
            </p>
          ) : (
            /* M19-T07 — a scroller on a phone, the frame's 4-up grid from `lg`.
               Every new class is an `lg:` variant, so mobile output is
               byte-identical to before apart from the card's own contents. */
            <div className="no-scrollbar flex gap-2.5 overflow-x-auto pb-1 lg:grid lg:grid-cols-4 lg:gap-4 lg:overflow-visible">
              {/* ICU is pinned on purpose (M21-T01). The card can say any bed
                  type now, but the home has no type selector — it is the
                  unfiltered front door, and ICU is the count the owner asked
                  for in M19-T07. */}
              {nearby.map((h) => (
                <MiniHospitalCard
                  key={h.id}
                  name={h.name}
                  area={miniArea(h)}
                  slug={h.slug}
                  emergencyStatus={h.beds.emergency_status}
                  bedType={ICU_BED_TYPE}
                  bedFree={h.beds.icu_free}
                  onSelect={() => navigate(`/hospital/${h.slug}`)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* NO CONCIERGE BLOCK HERE, and that is a decision rather than an
          oversight. M17-T07 ported `FgConciergeCall` from
          `designs/screens-figma-home.jsx` (~148) as a call CTA; the owner
          removed it on 2026-08-09.

          The reason is ADR-0026's, not a matter of taste: the Local front door
          is meant to carry near-zero reading — recognition over recall for
          stressed and low-literacy users — and the block added three lines of
          body copy to it. The tiles are the navigation.

          What upstream draws in this slot (`78:548`) is a free-text intake with
          a Send button and a 24-hour reply promise. That is lead capture, and
          lead capture stays Tourism's (`TourismEnquiry.tsx` plus the admin
          enquiries queue). Local home has no concierge entry point at all, by
          intent, and `LocalHome.test.tsx` guards the page against growing one. */}

      <HowItWorks />

      <Footer />
    </div>
  )
}

/**
 * The four steps, in the frame's order and topics, with the transactional verb
 * taken out of each (ADR-0015 — Phase 1 is read/search).
 *
 * The frame reads "Book a lab test" and "Quick guide on how to book for a
 * hospital bed"; `designs/screens-figma-home.jsx` had already reworded those
 * and this takes its wording. It deviates from `designs/` on step 2 only:
 * designs replaced the frame's "Find a specialist" with "Check live
 * availability", which no longer works now that the step carries the frame's
 * own artwork — the picture is a specialist search. The frame's topic is kept
 * and only its verb changed, which is the smaller edit anyway.
 *
 * EVERY STEP NAMES SOMETHING THAT EXISTS TODAY, checked route by route:
 * search and results at `/`, `/specialists`, `/diagnostics`, and the Directions
 * link on `/hospital/:slug` (`LocalProfile.tsx:213`, a Maps deep link — it does
 * not need the deferred Maps API). A step with no route is a promise.
 */
const STEPS = [
  {
    n: '1',
    title: 'Search for a hospital',
    body: 'Find partner hospitals near you by bed type, specialty or procedure.',
    image: 'how-it-works/searchforhospital',
  },
  {
    n: '2',
    title: 'Find a specialist',
    body: 'Browse specialists by hospital, the languages they speak and when they hold clinic.',
    image: 'how-it-works/findspecialist',
  },
  {
    n: '3',
    title: 'Find a lab test',
    body: 'Browse diagnostics and procedures with prices, across all ten partners.',
    image: 'how-it-works/bookalabtest',
  },
  {
    n: '4',
    title: 'Get directions',
    body: 'Open the route to the hospital, or call the ward before you travel.',
    image: 'how-it-works/getdirections',
  },
] as const

function HowItWorks(): ReactNode {
  return (
    <section
      aria-labelledby="how-h"
      className="px-4 pb-10 pt-2 sm:px-8 lg:px-12"
    >
      <h2
        id="how-h"
        className="mb-6 font-display text-d-xs font-medium tracking-display text-ink-1"
      >
        How it works
      </h2>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s) => (
          <div key={s.n} className="flex flex-col gap-2">
            {/* DECORATIVE, deliberately. These are device mockups of the app —
                placeholder artwork illustrating the workflow, per the owner's
                2026-08-17 ruling — and the title and body beside each already
                say what it shows. Describing them would put a mocked interface
                into the accessibility tree as though it were product copy,
                which is the one way their inaccuracy could leak into content. */}
            <Image
              decorative
              asset={asset(s.image)}
              className="aspect-[800/494] w-full rounded-r3 bg-bg-soft"
            />
            <span
              // The order is carried by reading order and by the title, so the
              // numeral is not announced twice. Matches Tourism exactly.
              aria-hidden="true"
              className="grid h-8 w-8 place-items-center rounded-full bg-accent text-[14px] font-medium text-accent-on"
            >
              {s.n}
            </span>
            <span className="text-[18px] font-medium leading-7 text-ink-1">
              {s.title}
            </span>
            <span className="text-[16px] leading-6 text-ink-3">{s.body}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function miniArea(h: HospitalListItem): string {
  return h.distance_km != null ? `${h.city} · ${h.distance_km} km` : h.city
}

function SuggestionRow({
  icon,
  onClick,
  children,
}: {
  icon: 'user' | 'flask'
  onClick: () => void
  children: ReactNode
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      // M17-T07 — rounded-[10px] was correct only while --radius-r3 was 10px.
      className="flex items-center gap-2 rounded-r3 border border-line-1 bg-bg-app px-3 py-2 text-left text-[13px] font-medium text-accent-ink hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Icon name={icon} size={14} />
      <span className="flex-1">{children}</span>
      <Icon name="arrow" size={14} />
    </button>
  )
}
